import clsx from "clsx";
import React, { FunctionComponent } from "react";

import type {
  ContentCardBaseConfig,
  ContentCardArticle,
} from "@ui/components/content-cards/content-cards.types";
import ContentBlock from "@ui/components/content-cards/ContentBlock/ContentBlock";
import ContentCardScore from "@ui/components/content-cards/ContentCardScore/ContentCardScore";
import Image from "@ui/components/media/Image";
import MediaIcon from "@ui/components/media/MediaIcon";
import { thumbnailContainerContext } from "@ui/components/media/MediaIcon/MediaIcon.css";
import CommentCallout from "@ui/components/meta/CommentCallout";
import DuetLink from "@ui/components/navigation/DuetLink";
import SELECTORS from "@ui/config/class-selectors.config";
import { useAnalyticsEventMetadataContext } from "@ui/context/analytics-event-metadata-context";
import {
  combineComponentContexts,
  useComponentConfig,
} from "@ui/context/component-config-context";
import { useContentCardAttributeContext } from "@ui/context/content-card-attribute-context";
import { useDataAttributes } from "@ui/hooks/use-data-attributes";
import useDefaults from "@ui/hooks/use-defaults";
import { sharedStyles } from "@ui/shared-styles";
import { getContentCardPosition } from "@ui/utils/get-content-card-position";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useGlobalDataContext } from "@voxmedia/duet-context/context/global-data-context";
import { getPageType } from "@voxmedia/duet-utils/analytics";

import * as styles from "./ProminentContentCard.css";

export interface ProminentContentCardBaseConfig extends ContentCardBaseConfig {
  borderVariant?: "prominent" | "standard";
  extraContentBlockStyles?: string;
  extraImageStyles?: string;
  className?: string;
  headlineClassName?: string;
  dekClassName?: string;
  imageAspectRatio:
    | "square"
    | "squareLarge"
    | "semiWide"
    | "wide"
    | "fourThree";
  imageTreatment?: keyof typeof styles.imageTreatment | null;
  imageOverlay?: keyof typeof styles.imageOverlay | null;
  imageSquareSizes?: string;
  lastChild?: boolean;
  overlap?: boolean;
  overlapStyle?: keyof typeof styles.overlap | null;
  showBorder?: boolean;
  logo?: React.ReactNode;
  imagePosition: "imageAbove" | "imageBelow";
  imageLeftTablet: boolean;
}

interface Props
  extends ContentCardArticle, Partial<ProminentContentCardBaseConfig> {
  componentContext?: string;
  gaComponentName: string;
  showBorderOnLastContentCard?: boolean;
  priorityImage?: boolean;
}

const ProminentContentCard: FunctionComponent<Props> = (props) => {
  const { componentContext = "river" } = props;
  const childComponentContext = combineComponentContexts([
    "prominentContentCard",
    componentContext,
  ]);

  const contentCardConfig = useComponentConfig(
    "prominentContentCard",
    componentContext,
  );

  const analytics = useAnalytics();
  const { metadata: analyticsMetadata } = useAnalyticsEventMetadataContext();

  const { placeholderImageUrl } = useGlobalDataContext().siteSettings;

  const {
    adFlag,
    advertiserName,
    advertiserPreamble,
    authors,
    authorByLine,
    category,
    chorusId,
    chorusUuid,
    className,
    commentCountId,
    commentUrl,
    comments,
    contentBlockSize,
    contentBlockVariation,
    contentType,
    coverLink,
    crossPostSource,
    dek,
    dekClassName,
    extraContentBlockStyles,
    extraImageStyles,
    gaComponentName,
    globalId,
    headline,
    headlineClassName,
    id,
    imageAspectRatio,
    imageTreatment,
    imageOverlay,
    imageSquareSizes,
    lastChild,
    live,
    liveBadgeName,
    lowerMetaFirstComponent,
    lowerMetaSecondComponent,
    lowerMetaThirdComponent,
    mediaIconInset,
    mediaVariation,
    mediaVariationIconPlacement,
    metaVariation,
    overlap,
    overlapStyle = "default",
    packageName,
    permalink,
    postTemplate,
    priorityImage,
    score,
    showAuthorBy,
    showCommentCallout,
    showBorder,
    showBorderOnLastContentCard,
    borderVariant = "prominent",
    showDek,
    showScore,
    site,
    streamName,
    thumbnails,
    timestamp,
    upperMetaFirstComponent,
    upperMetaSecondComponent,
    upperMetaThirdComponent,
    logo,
    imagePosition,
    imageLeftTablet,
  } = useDefaults(props, contentCardConfig);

  let upperMetaFirstComponentResolved = upperMetaFirstComponent;

  // Automatically determine first upper meta component if not specified.
  if (upperMetaFirstComponentResolved === undefined) {
    if (adFlag) {
      upperMetaFirstComponentResolved = "ContentBlockAdvertiserFlag";
    } else if (contentType === "map") {
      upperMetaFirstComponentResolved = "ContentBlockMap";
    } else if (packageName) {
      upperMetaFirstComponentResolved = "ContentBlockPackage";
    } else if (crossPostSource && !site?.isCrossCommunity) {
      upperMetaFirstComponentResolved = "ContentBlockCrossPost";
    } else if (live) {
      upperMetaFirstComponentResolved = "LiveBadge";
    } else {
      upperMetaFirstComponentResolved = "Category";
    }
  }

  const contentBlock = (
    <div className={clsx(overlap && styles.overlap[overlapStyle || "default"])}>
      <div className={extraContentBlockStyles || styles.contentBlockLayout}>
        <ContentBlock
          adFlag={adFlag}
          advertiserName={advertiserName}
          advertiserPreamble={advertiserPreamble}
          authorByLine={authorByLine}
          authors={authors}
          category={category}
          chorusId={chorusId}
          chorusUuid={chorusUuid}
          commentCountId={commentCountId}
          commentUrl={commentUrl}
          componentContext={childComponentContext}
          contentBlockSize={contentBlockSize}
          contentBlockVariation={contentBlockVariation}
          contentType={contentType}
          coverLink={coverLink}
          crossPostSource={crossPostSource}
          dek={dek}
          dekClassName={dekClassName}
          gaComponentName={gaComponentName}
          globalId={globalId}
          headline={headline}
          headlineClassName={headlineClassName}
          id={id}
          live={live}
          liveBadgeName={liveBadgeName}
          lowerMetaFirstComponent={lowerMetaFirstComponent}
          lowerMetaSecondComponent={lowerMetaSecondComponent}
          lowerMetaThirdComponent={lowerMetaThirdComponent}
          mediaVariation={mediaVariation}
          mediaVariationIconPlacement={mediaVariationIconPlacement}
          metaVariation={metaVariation}
          packageName={packageName}
          permalink={permalink}
          postTemplate={postTemplate}
          showAuthorBy={showAuthorBy}
          showDek={showDek}
          site={site}
          streamName={streamName}
          timestamp={timestamp}
          upperMetaFirstComponent={upperMetaFirstComponentResolved}
          upperMetaSecondComponent={upperMetaSecondComponent}
          upperMetaThirdComponent={upperMetaThirdComponent}
        />
      </div>
    </div>
  );

  const thumbnail =
    imageAspectRatio === "square" ? (
      // Show a horizontal image on mobile, square image on tablet/desktop,
      <>
        <Image
          src={thumbnails?.horizontal || placeholderImageUrl.horizontal}
          alt={headline}
          priority={!!priorityImage}
          sizes="100vw"
          className={sharedStyles.layout.hideTabletPlus}
        />
        <Image
          src={thumbnails?.square || placeholderImageUrl.square}
          alt={headline}
          priority={!!priorityImage}
          sizes={imageSquareSizes || "250px"}
          className={sharedStyles.layout.showTabletPlus}
        />
      </>
    ) : (
      <Image
        src={thumbnails?.horizontal || placeholderImageUrl.horizontal}
        alt={headline}
        priority={!!priorityImage}
        sizes="(max-width: 768px) 100vw, (max-width: 1180px) 50vw, 640px"
      />
    );

  const { generateContentCardAttributes } = useContentCardAttributeContext();

  return (
    <div
      className={clsx(
        SELECTORS.ContentCard,
        showBorder && (!lastChild || showBorderOnLastContentCard)
          ? borderVariant === "standard"
            ? styles.standardBorder
            : styles.border
          : "",
        imageLeftTablet
          ? styles.prominentCardLayout.imageLeftTablet
          : styles.prominentCardLayout.default,
        coverLink
          ? sharedStyles.contentCard.coverLinkContentCardContainer
          : sharedStyles.contentCard.contentCardContainer,
        className,
      )}
      {...useDataAttributes(props)}
      {...generateContentCardAttributes(props)}
    >
      {imagePosition === "imageBelow" && contentBlock}
      {logo}
      <div
        className={clsx(
          imageLeftTablet
            ? styles.imageLayout.imageLeftTablet
            : styles.imageLayout.default,
          styles.imageAspectRatio[imageAspectRatio],
          imageTreatment && styles.imageTreatment[imageTreatment],
          imageOverlay && styles.imageOverlay[imageOverlay],
          !imageTreatment && !imageOverlay && styles.borderRadius,
          thumbnailContainerContext,
          extraImageStyles,
        )}
      >
        {coverLink ? (
          thumbnail
        ) : (
          <DuetLink
            href={permalink}
            hrefSiteData={site}
            className={sharedStyles.contentCard.thumbnailLink}
            onClick={(e) => {
              analytics?.emit({
                event: "article_click",
                article_click: {
                  article_component_name: gaComponentName,
                  article_uri: permalink,
                  article_title: headline,
                  author: authors[0]?.name,
                  page_type: getPageType(contentType, !!postTemplate),
                  position: getContentCardPosition(e.currentTarget),
                  ...analyticsMetadata,
                },
              });
            }}
          >
            {thumbnail}
          </DuetLink>
        )}
        {mediaVariation && mediaVariationIconPlacement === "thumbnail" && (
          <MediaIcon
            placement="thumbnail"
            variation={mediaVariation}
            inset={mediaIconInset}
          />
        )}
        {score && showScore && <ContentCardScore score={score} />}
        {comments?.enabled && showCommentCallout && commentCountId && (
          <CommentCallout countId={commentCountId} />
        )}
      </div>
      {imagePosition === "imageAbove" && contentBlock}
    </div>
  );
};

export default ProminentContentCard;
